import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { rootUiTypes } from 'app-components/flux/root-ui';
import { rootUiActions } from 'app-components/flux/root-ui';
import { useAppSelector } from 'hooks/redux';
import { Map as ImmutableMap } from 'immutable';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { createAction } from 'redux-actions';

export function useUrlDialog<TDialogProps extends Record<string, any> = Record<string, any>>() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlDialogFF = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'urlDialog'));
  const dispatch = useDispatch();

  const openDialog = useCallback(
    (
      dialogType: string,
      dialogProps: TDialogProps,
      additionalProps: { rootUi?: boolean; replace?: boolean } = { rootUi: false, replace: false },
    ) => {
      const { rootUi, replace } = additionalProps;
      if (!urlDialogFF || rootUi) {
        // Fallback to dispatching an action if the feature flag is not enabled
        const actionCreator = createAction(rootUiActions.createDialog.toString(), () =>
          rootUiTypes.mkRootUiData({
            type: dialogType,
            id: dialogProps.id || `dialog-${Date.now()}`,
            props: ImmutableMap(dialogProps),
          }),
        );
        const action = actionCreator();
        dispatch(action);
        return;
      }

      const existingUrlDialogsProps = searchParams.get('dialogs');
      let existingUrlPropsArr: any[] = [];
      try {
        existingUrlPropsArr = existingUrlDialogsProps ? JSON.parse(decodeURIComponent(existingUrlDialogsProps)) : [];
        const alteredProps = { ...dialogProps, createdAt: Date.now(), dialogType };

        setSearchParams(
          (prev) => {
            const newParams = new URLSearchParams(prev);
            if (existingUrlPropsArr.length > 0) {
              existingUrlPropsArr.push(alteredProps); // Add new dialog props
            } else {
              existingUrlPropsArr.push(alteredProps);
            }
            newParams.set('dialogs', encodeURIComponent(JSON.stringify(existingUrlPropsArr)));

            return newParams;
          },
          { replace: replace ?? false },
        );
      } catch (e) {
        assert(false, 'Error parsing existing dialog props:', e);
      }
    },
    [setSearchParams, searchParams, urlDialogFF, dispatch],
  );

  const closeDialog = useCallback(
    (id?: string, replace?: boolean) => {
      const existingUrlDialogsProps = searchParams.get('dialogs');
      let existingUrlPropsArr: any[] = [];
      try {
        existingUrlPropsArr = existingUrlDialogsProps ? JSON.parse(decodeURIComponent(existingUrlDialogsProps)) : [];
        setSearchParams(
          (prev) => {
            const newParams = new URLSearchParams(prev);

            const idIndex = existingUrlPropsArr.findIndex((props: Record<string, any>) => props.id === id);
            // Copy the array so you don't mutate outside
            const updatedArr = [...existingUrlPropsArr];
            if (idIndex !== -1) {
              updatedArr.splice(idIndex, 1);
            } else if (updatedArr.length > 1) {
              updatedArr.pop();
            }

            if (updatedArr.length === 0) {
              newParams.delete('dialogs');
            } else {
              newParams.set('dialogs', encodeURIComponent(JSON.stringify(updatedArr)));
            }
            return newParams;
          },
          { replace: replace ?? false },
        );
      } catch (e) {
        assert(false, 'Error parsing existing dialog props:', e);
      }
    },
    [setSearchParams, searchParams],
  );

  return { openDialog, closeDialog };
}
